import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { KeyboardArrowDown, KeyboardArrowRight, RestartAlt, ShowChart, ViewList } from "@mui/icons-material";
import { Box, ButtonBase, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useUser } from "@framework/provider-user";
import type { IUserDto } from "@framework/provider-user";

import { EmptyPlaceholder } from "../../../../../../components/common/empty-placeholder";
import { MultilineChart } from "../../../scheme/components/analytics/components/multiline-chart";
import type { IChartData } from "../../../scheme/components/analytics/interfaces";
import { formatMeasurementNumber } from "../../../scheme/utils/measurement-format";
import { MultiAnalysisPanel, getMultiAnalysisExpandablePanelSx } from "../panel";
import { filterTrendSeriesByRange } from "../../utils/trend-series";
import type { IMultiAnalysisTimeRange } from "../../utils/trend-series";
import {
  buildTrendMeasureListItems,
  filterTrendSeriesByHiddenMeasureKeys,
  type IMultiAnalysisTrendMeasureListItem,
  type IMultiAnalysisTrendMeasureValue,
} from "../../utils/trend-measure-list";

interface IMultiAnalysisTrendsPanelProps {
  series: IChartData[];
  range: IMultiAnalysisTimeRange;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  hiddenMeasureKeys: ReadonlySet<string>;
  onToggleMeasureVisibility: (measureKey: string) => void;
  expandedFlexGrow?: number;
  isLoading?: boolean;
  frequencyHz?: number | null;
  editContent?: ReactNode;
  sx?: SxProps<Theme>;
}

const MULTI_ANALYSIS_TRENDS_LEGEND_ID = "multi-analysis-trends-legend";
const TRENDS_PANEL_MIN_EXPANDED_HEIGHT = 140;
export const TRENDS_PANEL_CHART_MIN_HEIGHT = TRENDS_PANEL_MIN_EXPANDED_HEIGHT;

const formatListValue = (
  item: IMultiAnalysisTrendMeasureValue | null,
  dataFormat: IUserDto["interfaceSettings"]["dataFormat"],
): string => (item ? formatMeasurementNumber(item.value, item.unitCode, dataFormat, { fallbackDigits: 2 }) : "-");

const TrendMeasureListItem: FC<{
  item: IMultiAnalysisTrendMeasureListItem;
  isVisible: boolean;
  dataFormat: IUserDto["interfaceSettings"]["dataFormat"];
  onToggle: (key: string) => void;
}> = ({ item, isVisible, dataFormat, onToggle }) => {
  return (
    <ButtonBase
      aria-pressed={isVisible}
      onClick={() => onToggle(item.key)}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        px: 1,
        py: 0.75,
        textAlign: "left",
        borderBottom: "1px solid #edf0f7",
        opacity: isVisible ? 1 : 0.45,
        bgcolor: isVisible ? "#fff" : "#f6f7fb",
        "&:hover": {
          bgcolor: "#f0f4fc",
        },
      }}
    >
      <Box
        sx={{
          flex: "0 0 auto",
          width: 10,
          height: 10,
          mt: 0.45,
          borderRadius: "50%",
          bgcolor: item.color,
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#2e3445" }} noWrap>
          {item.pointName}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#667085" }} noWrap>
          {item.measureName}
        </Typography>
        <Stack direction="row" spacing={1.25} sx={{ mt: 0.5, minWidth: 0, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 11, color: "#2e3445", fontWeight: 600 }}>
            V1: {formatListValue(item.value1, dataFormat)}
          </Typography>
          {item.value2 ? (
            <Typography sx={{ fontSize: 11, color: "#2e3445", fontWeight: 600 }}>
              V2: {formatListValue(item.value2, dataFormat)}
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </ButtonBase>
  );
};

export const MultiAnalysisTrendsPanel: FC<IMultiAnalysisTrendsPanelProps> = ({
  series,
  range,
  collapsed,
  onCollapsedChange,
  hiddenMeasureKeys,
  onToggleMeasureVisibility,
  expandedFlexGrow = 1,
  isLoading = false,
  frequencyHz = null,
  editContent,
  sx,
}) => {
  const { formatMessage } = useIntl();
  const user = useUser<IUserDto>();
  const dataFormat = user.profile.interfaceSettings.dataFormat;
  const [resetZoomHandler, setResetZoomHandler] = useState<(() => void) | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [measureListOpen, setMeasureListOpen] = useState(false);
  const [cursorDate, setCursorDate] = useState<string | null>(null);
  const filteredSeries = useMemo(() => filterTrendSeriesByRange(series, range), [range, series]);
  const visibleSeries = useMemo(
    () => filterTrendSeriesByHiddenMeasureKeys(filteredSeries, hiddenMeasureKeys),
    [filteredSeries, hiddenMeasureKeys],
  );
  const measureListItems = useMemo(
    () => buildTrendMeasureListItems(filteredSeries, cursorDate),
    [cursorDate, filteredSeries],
  );
  const measureListToggleLabel = formatMessage({
    id: measureListOpen
      ? "pages.multiAnalysis.trendsPanel.hideMeasureList"
      : "pages.multiAnalysis.trendsPanel.showMeasureList",
  });
  const hasTrendData = filteredSeries.length > 0;
  const hasVisibleTrendData = visibleSeries.length > 0;
  const formattedFrequency =
    frequencyHz != null && Number.isFinite(frequencyHz)
      ? formatMeasurementNumber(frequencyHz, 3, dataFormat, { fallbackDigits: 2 })
      : null;

  const handleRegisterReset = useCallback((handler: (() => void) | null) => {
    setResetZoomHandler(handler ? () => handler : null);
  }, []);

  const handleResetZoom = useCallback(() => {
    resetZoomHandler?.();
  }, [resetZoomHandler]);

  useEffect(() => {
    setCursorDate(null);
  }, [filteredSeries]);

  return (
    <MultiAnalysisPanel
      titleId={
        formattedFrequency
          ? "pages.multiAnalysis.trendsPanel.frequencyProfileTitle"
          : "pages.multiAnalysis.trendsPanel.title"
      }
      titleValues={formattedFrequency ? { frequency: formattedFrequency } : undefined}
      icon={<ShowChart fontSize="small" />}
      sx={
        collapsed
          ? getMultiAnalysisExpandablePanelSx(collapsed, TRENDS_PANEL_MIN_EXPANDED_HEIGHT, expandedFlexGrow)
          : [
              getMultiAnalysisExpandablePanelSx(collapsed, TRENDS_PANEL_MIN_EXPANDED_HEIGHT, expandedFlexGrow),
              ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]
      }
      actions={
        <Stack direction="row" spacing={0.5} alignItems="center">
          {isZoomed && resetZoomHandler ? (
            <Tooltip title={formatMessage({ id: "pages.multiAnalysis.trendsPanel.resetZoom" })}>
              <span>
                <IconButton size="small" onClick={handleResetZoom}>
                  <RestartAlt fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {editContent ? null : (
            <Tooltip title={measureListToggleLabel}>
              <span>
                <IconButton
                  aria-label={measureListToggleLabel}
                  size="small"
                  color={measureListOpen ? "primary" : "default"}
                  disabled={!hasTrendData}
                  onClick={() => setMeasureListOpen(value => !value)}
                >
                  <ViewList fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton size="small" onClick={() => onCollapsedChange(!collapsed)}>
            {collapsed ? <KeyboardArrowRight fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
          </IconButton>
        </Stack>
      }
    >
      {collapsed ? null : (
        <ProgressOverlay
          isLoading={editContent ? false : isLoading}
          wrapperSx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}
        >
          {editContent ? (
            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>{editContent}</Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowX: "hidden",
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  minHeight: `max(100%, ${TRENDS_PANEL_CHART_MIN_HEIGHT}px)`,
                  height: "100%",
                }}
              >
                {hasTrendData ? (
                  <Stack direction={{ xs: "column", md: "row" }} sx={{ height: "100%", minHeight: 0, minWidth: 0 }}>
                    <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                      {hasVisibleTrendData ? (
                        <MultilineChart
                          captureSelectionOutsideCanvas
                          data={visibleSeries}
                          chartTitle={formatMessage({ id: "pages.multiAnalysis.trendsPanel.title" })}
                          legendContainerId={MULTI_ANALYSIS_TRENDS_LEGEND_ID}
                          hideLegend
                          registerReset={handleRegisterReset}
                          onZoomStateChange={setIsZoomed}
                          onCursorDateChange={setCursorDate}
                          timeBounds={range}
                        />
                      ) : (
                        <EmptyPlaceholder
                          icon={<ShowChart />}
                          text={formatMessage({ id: "pages.multiAnalysis.trendsPanel.empty" })}
                          wrapperProps={{ sx: { height: "100%" } }}
                        />
                      )}
                    </Box>
                    {measureListOpen ? (
                      <Box
                        sx={{
                          flex: { xs: "0 0 auto", md: "0 0 320px" },
                          width: { xs: "100%", md: 320 },
                          maxHeight: { xs: 220, md: "100%" },
                          minHeight: 0,
                          borderTop: { xs: "1px solid #d8ddea", md: 0 },
                          borderLeft: { xs: 0, md: "1px solid #d8ddea" },
                          bgcolor: "#fff",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        {measureListItems.length > 0 ? (
                          measureListItems.map(item => (
                            <TrendMeasureListItem
                              key={item.key}
                              item={item}
                              isVisible={!hiddenMeasureKeys.has(item.key)}
                              dataFormat={dataFormat}
                              onToggle={onToggleMeasureVisibility}
                            />
                          ))
                        ) : (
                          <Typography sx={{ p: 1.5, fontSize: 12, color: "#8b93a7" }}>
                            {formatMessage({ id: "pages.multiAnalysis.trendsPanel.emptyMeasureList" })}
                          </Typography>
                        )}
                      </Box>
                    ) : null}
                  </Stack>
                ) : (
                  <EmptyPlaceholder
                    icon={<ShowChart />}
                    text={formatMessage({ id: "pages.multiAnalysis.trendsPanel.empty" })}
                    wrapperProps={{ sx: { height: "100%" } }}
                  />
                )}
              </Box>
            </Box>
          )}
        </ProgressOverlay>
      )}
    </MultiAnalysisPanel>
  );
};
